import { Box, Row } from '@stenajs-webui/core';
import type { FC } from 'react';
import type { BasicBooking } from '../../../../../common/graphql/fragments/gql/BasicBooking';
import { useAnimalTypesOptions } from '../../../../../common/graphql/useAnimalTypesOptions';
import type { TrackerCategory } from '../../../../../common/tracking/trackerService';
import { includeYesOption } from '../../../../../common/utils/animalsUtils';
import { getApprovalInfo } from '../../../../../common/utils/approvalInfo';
import { type BookingFields, isHidden } from '../../../../../common/utils/bookingFields';
import { type GoodsApproval, GoodsApprovalType } from '../../../../../common/utils/GoodsApproval';
import { resolveHazardousGoodsApproved } from '../../../../../common/utils/hazardousGoodsUtil';
import {
  transformAnimalOption,
  transformYesNoOption,
} from '../../../../../common/utils/optionTransformers';
import { ApproveAlert } from '../../../../../common-ui/ApproveAlert';
import { DetailsFieldCell } from '../../../../../common-ui/details/DetailsFieldCell';
import { HiddenFieldGate } from '../../../../../common-ui/HiddenFieldGate';
import { ColumnId } from '../../../../../gql/graphql';
import { DropdownField } from '../fields/DropdownField';
import { SenderReceiverCountryField } from '../fields/SenderReceiverCountryField';
import { yesNoOptions } from './common';

const trackerCategory: TrackerCategory = 'Details';

interface CargoDeclarationSectionProps {
  booking: BasicBooking;
  disabled: boolean;
  fieldsToEnable: BookingFields;
  fieldsToShow: BookingFields;
}

export const CargoDetailsSection: FC<CargoDeclarationSectionProps> = ({
  booking,
  fieldsToShow,
  fieldsToEnable,
  disabled,
}) => {
  const [animalTypesOptions] = useAnimalTypesOptions({
    includeNoOption: true,
    includeYesOption: includeYesOption(booking.livestockType, booking.livestock),
  });

  const hazardousGoodsApproval: GoodsApproval = {
    status: resolveHazardousGoodsApproved(
      false,
      booking.hazardousGoods,
      booking.hazardousGoodsApproved,
    ),
    type: GoodsApprovalType.Hazardous,
  };

  const approvalInfo = getApprovalInfo(hazardousGoodsApproval);

  const isFieldDisabled = (field: ColumnId) => disabled || isHidden(fieldsToEnable, field);

  return (
    <Row flexWrap={'wrap'} gap={2}>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.HazardousGoods)}>
        <DetailsFieldCell
          width={['100%', null, 'auto']}
          label={'Hazardous goods'}
          renderField={id => (
            <Row gap alignItems={'center'}>
              <Box width={['100%', null, 80]}>
                <DropdownField
                  id={id}
                  name={'hazardousGoods'}
                  trackerCategory={trackerCategory}
                  transformOption={transformYesNoOption}
                  options={yesNoOptions}
                  disabled={isFieldDisabled(ColumnId.HazardousGoods)}
                />
              </Box>
              {approvalInfo && <ApproveAlert config={approvalInfo} />}
            </Row>
          )}
        />
      </HiddenFieldGate>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.LivestockTypeCode)}>
        <DetailsFieldCell
          width={['100%', null, 150]}
          label={'Animals'}
          renderField={id => (
            <DropdownField
              id={id}
              trackerCategory={trackerCategory}
              name={'livestockType'}
              transformOption={transformAnimalOption}
              disabled={isFieldDisabled(ColumnId.LivestockTypeCode)}
              options={animalTypesOptions}
            />
          )}
        />
      </HiddenFieldGate>
      <Row gap={2} flex={1}>
        <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.SenderCountryCode)}>
          <DetailsFieldCell
            width={['100%', null, 200]}
            label={'Sender country'}
            renderField={id => (
              <SenderReceiverCountryField
                id={id}
                name={'senderCountry'}
                trackerCategory={trackerCategory}
                disabled={isFieldDisabled(ColumnId.SenderCountryCode)}
              />
            )}
          />
        </HiddenFieldGate>
        <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.ReceiverCountryCode)}>
          <DetailsFieldCell
            width={['100%', null, 200]}
            label={'Receiver country'}
            renderField={id => (
              <SenderReceiverCountryField
                id={id}
                name={'receiverCountry'}
                trackerCategory={trackerCategory}
                disabled={isFieldDisabled(ColumnId.ReceiverCountryCode)}
              />
            )}
          />
        </HiddenFieldGate>
      </Row>
    </Row>
  );
};
